/**
 * Mojibake (garbled text) repair utility
 * Fixes UTF-8 text that was incorrectly decoded as Latin-1
 * 
 * This happens when UTF-8 bytes are interpreted as Latin-1/ISO-8859-1
 */

// Mojibake detection patterns (UTF-8 misread as Latin-1)
const MOJIBAKE_PATTERNS = /[ØÙÚÛÜÝÞßàáâãäåæçèéêëìíîïðñòóôõö÷øùúûüýþÿĀ-ſ]/;
const ARABIC_MOJIBAKE = /Ø[§¨©ª«¬®¯°±²³´µ¶·¸¹º»¼½¾¿ÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖרÙÚÛÜÝÞß]/;
const HEBREW_MOJIBAKE = /×[×ŸŠšŒœŽž]/;

// Valid Arabic Unicode range
const ARABIC_RANGE = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;
// Valid Hebrew Unicode range  
const HEBREW_RANGE = /[\u0590-\u05FF]/;

/**
 * Check if string likely contains mojibake (garbled UTF-8)
 */
function hasMojibake(s: string): boolean {
  if (!s || typeof s !== 'string') return false;
  // Quick check for common mojibake patterns
  return MOJIBAKE_PATTERNS.test(s) && (ARABIC_MOJIBAKE.test(s) || HEBREW_MOJIBAKE.test(s));
}

/**
 * Try to repair Latin-1 misinterpreted UTF-8
 * Converts string back to bytes assuming Latin-1, then decodes as UTF-8
 */
function tryRepairLatin1Utf8(s: string): string {
  try {
    // Convert each character to its byte value (Latin-1 assumption)
    const bytes = new Uint8Array([...s].map(ch => ch.charCodeAt(0) & 0xff));
    // Decode as UTF-8
    const repaired = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
    return repaired;
  } catch {
    return s;
  }
}

/**
 * Check if repaired text is better than original
 * Better = has Arabic/Hebrew characters and fewer mojibake patterns
 */
function isRepairBetter(original: string, repaired: string): boolean {
  if (!repaired || repaired === original) return false;
  
  // Count mojibake patterns
  const origMojibake = (original.match(MOJIBAKE_PATTERNS) || []).length;
  const repairedMojibake = (repaired.match(MOJIBAKE_PATTERNS) || []).length;
  
  // Check for valid Arabic/Hebrew in repaired
  const hasValidChars = ARABIC_RANGE.test(repaired) || HEBREW_RANGE.test(repaired);
  
  // Repaired should have fewer mojibake patterns and contain valid Arabic/Hebrew
  return hasValidChars && repairedMojibake < origMojibake;
}

/**
 * Fix mojibake in a string
 */
function fixString(s: string): string {
  if (!s || typeof s !== 'string') return s;
  if (!hasMojibake(s)) return s;
  
  const repaired = tryRepairLatin1Utf8(s);
  return isRepairBetter(s, repaired) ? repaired : s;
}

/**
 * Recursively fix mojibake in any data structure
 * - Strings: attempt repair if mojibake detected
 * - Arrays: map over items
 * - Objects: fix string values (keys unchanged)
 * - Other types: return as-is
 */
export function fixMojibake<T>(input: T): T {
  // Null/undefined
  if (input === null || input === undefined) {
    return input;
  }

  // String
  if (typeof input === 'string') {
    return fixString(input) as T;
  }

  // Array
  if (Array.isArray(input)) {
    return input.map(item => fixMojibake(item)) as T;
  }

  // Object (but not Date, RegExp, etc.)
  if (typeof input === 'object' && input.constructor === Object) {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(input)) {
      // Only fix string values, leave keys unchanged
      result[key] = fixMojibake(value);
    }
    return result as T;
  }

  // Other types (number, boolean, Date, etc.)
  return input;
}

/**
 * Quick check if data likely contains mojibake
 * Used to skip processing on clean data for performance
 */
export function dataNeedsFix(data: unknown): boolean {
  if (!data) return false;
  try {
    const sample = JSON.stringify(data);
    // Quick scan for mojibake patterns
    return sample.includes('Ø') || sample.includes('Ù') || sample.includes('Ã') || sample.includes('Â');
  } catch {
    return false;
  }
}

/**
 * Apply mojibake fix only if needed (performance optimization)
 */
export function fixMojibakeIfNeeded<T>(data: T): T {
  if (!dataNeedsFix(data)) return data;
  return fixMojibake(data);
}
